import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  alpha,
  useTheme,
  Skeleton,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  Tooltip,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  EventNote,
  AutorenewOutlined,
  Close,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useModulePermissions } from '../hooks/useModulePermissions';
import eventService, { SchoolEvent, CreateEventData } from '../services/eventService';
import eventCategoryService, { EventCategory } from '../services/eventCategoryService';
import ConfirmationDialog from '../components/common/ConfirmationDialog';

const validationSchema = yup.object({
  title: yup.string().required('Title is required').max(200),
  description: yup.string().max(2000),
  date: yup.string().required('Date is required'),
  endDate: yup.string().nullable(),
  startTime: yup.string().nullable(),
  endTime: yup.string().nullable(),
  categoryId: yup.string().required('Category is required'),
  location: yup.string().max(200),
  isAllDay: yup.boolean(),
  isRecurring: yup.boolean(),
  status: yup.string(),
});

const STATUS_OPTIONS = [
  { value: 'UPCOMING', label: 'Upcoming', color: 'primary' as const },
  { value: 'COMPLETED', label: 'Completed', color: 'success' as const },
  { value: 'CANCELLED', label: 'Cancelled', color: 'error' as const },
];

const EventsPage: React.FC = () => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { canWrite } = useModulePermissions();
  const hasWrite = canWrite('events');

  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [events, setEvents] = useState<SchoolEvent[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [categories, setCategories] = useState<EventCategory[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingEvent, setEditingEvent] = useState<SchoolEvent | null>(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [eventToDelete, setEventToDelete] = useState<{ id: string; title: string; isParentRecurring: boolean } | null>(null);
  const [showMultiDay, setShowMultiDay] = useState(false);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { year };
      if (filterCategory) params.categoryId = filterCategory;
      if (filterStatus) params.status = filterStatus;
      const data = await eventService.getEvents(params);
      setEvents(data);
      setTotalCount(data.length);
    } catch (error: any) {
      enqueueSnackbar('Failed to fetch events', { variant: 'error' });
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  }, [year, filterCategory, filterStatus, enqueueSnackbar]);

  const fetchCategories = useCallback(async () => {
    try {
      const cats = await eventCategoryService.getCategories();
      setCategories(cats);
    } catch (error: any) {
      console.error('Failed to fetch categories:', error);
    }
  }, []);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);
  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const formik = useFormik<CreateEventData & { status: string }>({
    initialValues: {
      title: '',
      description: '',
      date: new Date().toISOString().split('T')[0],
      endDate: '',
      startTime: '',
      endTime: '',
      categoryId: '',
      location: '',
      isAllDay: true,
      isRecurring: false,
      status: 'UPCOMING',
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        const payload: any = { ...values };
        if (!showMultiDay) payload.endDate = undefined;
        if (values.isAllDay) {
          payload.startTime = undefined;
          payload.endTime = undefined;
        }

        if (editingEvent) {
          await eventService.updateEvent(editingEvent._id, payload);
          enqueueSnackbar('Event updated successfully', { variant: 'success' });
        } else {
          const { status, ...createPayload } = payload;
          await eventService.createEvent(createPayload);
          enqueueSnackbar('Event created successfully', { variant: 'success' });
        }
        handleCloseDialog();
        fetchEvents();
      } catch (error: any) {
        enqueueSnackbar(error.response?.data?.message || 'Failed to save event', { variant: 'error' });
      }
    },
  });

  const handleOpenDialog = (event?: SchoolEvent) => {
    if (event) {
      setEditingEvent(event);
      const catId = typeof event.categoryId === 'object' ? event.categoryId._id : event.categoryId;
      setShowMultiDay(!!event.endDate);
      formik.setValues({
        title: event.title,
        description: event.description || '',
        date: new Date(event.date).toISOString().split('T')[0],
        endDate: event.endDate ? new Date(event.endDate).toISOString().split('T')[0] : '',
        startTime: event.startTime || '',
        endTime: event.endTime || '',
        categoryId: catId,
        location: event.location || '',
        isAllDay: event.isAllDay,
        isRecurring: event.isRecurring,
        status: event.status,
      });
    } else {
      setEditingEvent(null);
      setShowMultiDay(false);
      formik.resetForm();
      formik.setFieldValue('date', new Date().toISOString().split('T')[0]);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingEvent(null);
    formik.resetForm();
  };

  const handleDeleteClick = (event: SchoolEvent) => {
    const isParentRecurring = event.isRecurring && !event.parentEventId;
    setEventToDelete({ id: event._id, title: event.title, isParentRecurring });
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (eventToDelete) {
      try {
        await eventService.deleteEvent(eventToDelete.id);
        enqueueSnackbar('Event deleted successfully', { variant: 'success' });
        setDeleteDialogOpen(false);
        setEventToDelete(null);
        fetchEvents();
      } catch (error: any) {
        enqueueSnackbar('Failed to delete event', { variant: 'error' });
      }
    }
  };

  const getCategoryInfo = (event: SchoolEvent) => {
    if (typeof event.categoryId === 'object' && event.categoryId) {
      return { name: event.categoryId.name, color: event.categoryId.color || '#6b7280' };
    }
    return { name: 'Unknown', color: '#6b7280' };
  };

  const isAutoGenerated = (event: SchoolEvent) => !!event.parentEventId;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'UPCOMING': return theme.palette.primary.main;
      case 'COMPLETED': return theme.palette.success.main;
      case 'CANCELLED': return theme.palette.error.main;
      default: return theme.palette.grey[500];
    }
  };

  const paginatedEvents = events.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  if (initialLoad && loading) {
    return (
      <Box>
        <Skeleton variant="text" width={250} height={40} sx={{ mb: 2 }} />
        <Skeleton variant="rounded" height={60} sx={{ mb: 3 }} />
        <Skeleton variant="rounded" height={400} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', mb: 3, gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} color="text.primary">
            Events
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage school events and activities
          </Typography>
        </Box>
        {hasWrite && (
          <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenDialog()}>
            Add Event
          </Button>
        )}
      </Box>

      {/* Filters */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Year</InputLabel>
              <Select
                value={year}
                label="Year"
                onChange={(e) => {
                  setYear(Number(e.target.value));
                  setPage(0);
                }}
              >
                {[year - 1, year, year + 1].map((y) => (
                  <MenuItem key={y} value={y}>{y}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Category</InputLabel>
              <Select
                value={filterCategory}
                label="Category"
                onChange={(e) => {
                  setFilterCategory(e.target.value as string);
                  setPage(0);
                }}
              >
                <MenuItem value="">All</MenuItem>
                {categories.map((cat) => (
                  <MenuItem key={cat._id} value={cat._id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: cat.color || '#6b7280' }} />
                      {cat.name}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filterStatus}
                label="Status"
                onChange={(e) => {
                  setFilterStatus(e.target.value as string);
                  setPage(0);
                }}
              >
                <MenuItem value="">All</MenuItem>
                {STATUS_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {!initialLoad && loading && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              bgcolor: alpha(theme.palette.background.paper, 0.6),
              zIndex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          />
        )}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Location</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Recurring</TableCell>
                {hasWrite && <TableCell align="center" sx={{ fontWeight: 600 }}>Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedEvents.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={hasWrite ? 7 : 6} sx={{ textAlign: 'center', py: 6 }}>
                    <EventNote sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body1" color="text.secondary">
                      {filterCategory || filterStatus
                        ? 'No events match your filters'
                        : `No events found for ${year}`}
                    </Typography>
                    {!filterCategory && !filterStatus && hasWrite && (
                      <Button
                        variant="outlined"
                        startIcon={<Add />}
                        onClick={() => handleOpenDialog()}
                        sx={{ mt: 2 }}
                      >
                        Create Event
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                paginatedEvents.map((event) => {
                  const cat = getCategoryInfo(event);
                  const statusColor = getStatusColor(event.status);
                  return (
                    <TableRow
                      key={event._id}
                      hover
                      sx={isAutoGenerated(event) ? { opacity: 0.85 } : undefined}
                    >
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Typography variant="body2">
                          {new Date(event.date).toLocaleDateString(undefined, {
                            weekday: 'short', year: 'numeric', month: 'short', day: 'numeric'
                          })}
                        </Typography>
                        {event.endDate && (
                          <Typography variant="caption" color="text.secondary">
                            to {new Date(event.endDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                          </Typography>
                        )}
                        {!event.isAllDay && event.startTime && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            {event.startTime}{event.endTime ? ` - ${event.endTime}` : ''}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="body2" fontWeight={500}>{event.title}</Typography>
                          {isAutoGenerated(event) && (
                            <Tooltip title="Auto-generated from recurring event">
                              <AutorenewOutlined sx={{ fontSize: 14, color: 'text.disabled' }} />
                            </Tooltip>
                          )}
                        </Box>
                        {event.description && (
                          <Tooltip title={event.description}>
                            <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 200, display: 'block' }}>
                              {event.description}
                            </Typography>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={cat.name}
                          size="small"
                          sx={{
                            bgcolor: alpha(cat.color, 0.1),
                            color: cat.color,
                            fontWeight: 600,
                            fontSize: '0.75rem',
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {event.location || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={STATUS_OPTIONS.find(s => s.value === event.status)?.label || event.status}
                          size="small"
                          sx={{
                            bgcolor: alpha(statusColor, 0.1),
                            color: statusColor,
                            fontWeight: 600,
                            fontSize: '0.75rem',
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        {event.isRecurring ? (
                          <Chip
                            label="Yearly"
                            size="small"
                            sx={{
                              bgcolor: alpha(theme.palette.secondary.main, 0.1),
                              color: theme.palette.secondary.main,
                              fontWeight: 600,
                              fontSize: '0.75rem',
                            }}
                          />
                        ) : null}
                      </TableCell>
                      {hasWrite && (
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                            <Tooltip title="Edit">
                              <IconButton size="small" onClick={() => handleOpenDialog(event)}>
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton size="small" color="error" onClick={() => handleDeleteClick(event)}>
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle component="div" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" fontWeight={600}>
            {editingEvent ? 'Edit Event' : 'Add Event'}
          </Typography>
          <IconButton size="small" onClick={handleCloseDialog}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box component="form" onSubmit={formik.handleSubmit}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Event Title"
                  name="title"
                  value={formik.values.title}
                  onChange={formik.handleChange}
                  error={formik.touched.title && Boolean(formik.errors.title)}
                  helperText={formik.touched.title && formik.errors.title}
                  placeholder="e.g., Annual Sports Day"
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  multiline
                  rows={3}
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  error={formik.touched.description && Boolean(formik.errors.description)}
                  helperText={formik.touched.description && formik.errors.description}
                  placeholder="Describe the event details (optional)"
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Date"
                  type="date"
                  name="date"
                  value={formik.values.date}
                  onChange={formik.handleChange}
                  InputLabelProps={{ shrink: true }}
                  error={formik.touched.date && Boolean(formik.errors.date)}
                  helperText={formik.touched.date && formik.errors.date}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={showMultiDay}
                      onChange={(e) => {
                        setShowMultiDay(e.target.checked);
                        if (!e.target.checked) formik.setFieldValue('endDate', '');
                      }}
                    />
                  }
                  label={<Typography variant="body2">Multi-day event</Typography>}
                />
              </Grid>
              {showMultiDay && (
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="End Date"
                    type="date"
                    name="endDate"
                    value={formik.values.endDate}
                    onChange={formik.handleChange}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              )}
              <Grid size={{ xs: 12 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formik.values.isAllDay}
                      onChange={formik.handleChange}
                      name="isAllDay"
                    />
                  }
                  label={<Typography variant="body2">All Day Event</Typography>}
                />
              </Grid>
              {!formik.values.isAllDay && (
                <>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Start Time"
                      type="time"
                      name="startTime"
                      value={formik.values.startTime}
                      onChange={formik.handleChange}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="End Time"
                      type="time"
                      name="endTime"
                      value={formik.values.endTime}
                      onChange={formik.handleChange}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </>
              )}
              <Grid size={{ xs: 12 }}>
                <FormControl
                  fullWidth
                  error={formik.touched.categoryId && Boolean(formik.errors.categoryId)}
                >
                  <InputLabel>Category</InputLabel>
                  <Select
                    name="categoryId"
                    value={formik.values.categoryId}
                    label="Category"
                    onChange={formik.handleChange}
                  >
                    {categories.map((cat) => (
                      <MenuItem key={cat._id} value={cat._id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: cat.color || '#6b7280' }} />
                          {cat.name}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                  {formik.touched.categoryId && formik.errors.categoryId && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                      {formik.errors.categoryId}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Location"
                  name="location"
                  value={formik.values.location}
                  onChange={formik.handleChange}
                  placeholder="e.g., School Auditorium"
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formik.values.isRecurring}
                      onChange={formik.handleChange}
                      name="isRecurring"
                    />
                  }
                  label={<Typography variant="body2">Recurring Yearly (Repeats every year on this date)</Typography>}
                />
              </Grid>
              {editingEvent && (
                <Grid size={{ xs: 12 }}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      name="status"
                      value={formik.values.status}
                      label="Status"
                      onChange={formik.handleChange}
                    >
                      {STATUS_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseDialog} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={() => formik.handleSubmit()}
            variant="contained"
            disabled={formik.isSubmitting}
          >
            {formik.isSubmitting ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <ConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => { setDeleteDialogOpen(false); setEventToDelete(null); }}
        onConfirm={handleDelete}
        title="Delete Event?"
        message={
          eventToDelete?.isParentRecurring
            ? `Are you sure you want to delete "${eventToDelete?.title}"? This will also delete all auto-generated recurring instances across all years.`
            : `Are you sure you want to delete "${eventToDelete?.title}"? This action cannot be undone.`
        }
        confirmText="Delete"
        cancelText="Cancel"
        confirmColor="error"
      />
    </Box>
  );
};

export default EventsPage;
